/*
 * Copyright (c) 2018-2019, F5 Networks, Inc. All rights reserved.
 *
 * No part of this software may be reproduced or transmitted in any
 * form or by any means, electronic or mechanical, for any purpose,
 * without express written permission of F5 Networks, Inc.
 */
import { DialogType } from 'apmui/components/dialog/Model';
import PageType from 'apmui/page/PageType';
import IWebtopModel from 'apmui/page/webtop/Model';
import ResourceType from 'apmui/resource/ResourceType';
import App from 'apmui/App';
import Object from 'apmui/util/Object';
import request from 'apmui/util/request';
import EventTags from 'apmui/EventTags';
import { IResourceProps } from 'apmui/resource';
import LaunchType from 'apmui/resource/LaunchType';
import { LaunchStatus } from 'apmui/resource/WebtopResourceModel';
import userAgent, { UserAgent } from 'apmui/util/UserAgent';
import { encode as Base64encode } from 'apmui/util/base64';
import createFrame from './createFrame';

import getCitrixClients from './CitrixClient';
import RemoteDesktopModel, { RdpModel, REMOTE_DESKTOP_TYPE as RdType, SERVERTYPE, isVdiResourceList } from './Model';
import VdiClientInfo, { VDI_CLIENT, LaunchFunction, IVdiClient, getUserPreferredClient } from './VdiClientInfo';
import { VdiErrorType } from './VdiError';
import { clearVisitedFavorites, markFavoriteAsVisited } from './VisitedFavorites';
import getViewClients from './VmwareViewClient';

const app = App.get();

const VDI = {
    timeout: 15000, // ms
    icon: '/public/images/full_wt/terminal_service.png',
};

async function launchRDResource(resource: RemoteDesktopModel, page: IWebtopModel, launchType?: LaunchType) {
    try {
        resource.status = LaunchStatus.IN_PROGRESS;

        if (isVdiResourceList(resource)) {
            // finally will be executed on return from try block
            return await launchResourceList(resource);
        }

        const client = await getPreferredClient(resource, page);
        if (client) {
            updateAppLaunchCount(resource, page);
            await client.launch(resource, launchType);
        }

        resource.status = LaunchStatus.CONNECTED;
    } catch (error) {
        if (error === EventTags.DIALOG_CLOSE) {
            /* Not an error; User closed some dialog.
             * Keep ERROR state if lastError has not been cleared.
             */
            resource.status = resource.lastError ? LaunchStatus.ERROR : LaunchStatus.INITIAL;
            return;
        }

        resource.status = LaunchStatus.ERROR;
        resource.lastError = error;

        // Do not show error message if the resource was auto-started
        if (launchType !== LaunchType.AUTOLAUNCH) {
            // don't wait here to ensure correct event sequence in 'try again' case
            app.showModal(DialogType.VDI_ERROR, { resource, error })
                .then(() => {
                    // try again
                    app.launchResource(resource, { launchType });
                })
                .catch(() => true);
        }
    } finally {
        app.dispatch(EventTags.RESOURCE_LAUNCH_COMPLETE, resource);
    }
}

/**
 * Clear logged-in resources state on logout
 */
app.subscribe(EventTags.LOGOUT, (event, state) => {
    clearVisitedFavorites();
    if (state.pageType !== PageType.WEBTOP) {
        return;
    }

    const rdResources = state.webtop.resources && state.webtop.resources[ResourceType.REMOTE_DESKTOP];
    if (!rdResources) {
        return;
    }
    /* Collect all resources that have apps launched and require logout */
    const resourcesWithAppsLaunched = Object.values(rdResources).filter(isLogoutRequired);
    /* Run logout requests for all matching resources */
    if (resourcesWithAppsLaunched.length > 0) {
        /* Stop event until logout is complete for all resources */
        event.preventDefault();
        event.stopPropagation();
        Promise.all(resourcesWithAppsLaunched.map(logoutRDResource))
            .catch(() => true)
            /* Restart logout sequence */
            .then(() => app.dispatch(EventTags.LOGOUT));
    }
});

function isLogoutRequired(resource: RemoteDesktopModel) {
    return resource.vdiLogoffUrl && resource.numOfAppsLaunched;
}

function logoutRDResource(resource: RemoteDesktopModel) {
    resource.numOfAppsLaunched = 0;
    if (resource.vdiLogoffUrl) {
        return request(resource.vdiLogoffUrl, { method: 'POST' }).catch(() => true);
    }
    return Promise.resolve(true);
}

function updateAppLaunchCount(resource: RemoteDesktopModel, page: IWebtopModel) {
    const parentResource =
        resource.parent &&
        page.webtop.resources &&
        page.webtop.resources[ResourceType.REMOTE_DESKTOP] &&
        page.webtop.resources[ResourceType.REMOTE_DESKTOP]![resource.parent];
    if (parentResource) {
        parentResource.numOfAppsLaunched++;
    }
}

function launchNativeRdpUri(uri: string) {
    const notFirefox = !userAgent.is(UserAgent.FIREFOX);
    window.open(uri, notFirefox ? '_self' : createFrame(RdType.RDP));
}

async function launchNativeRdp(resource: RdpModel) {
    // RDWeb generated resource
    let uri: string | undefined = resource.launchUri;

    if (!uri) {
        // standalone (session host) resource
        uri = `/f5vdi/${resource.rdType}/launch${resource.id}`;

        if (resource.userDefinedDst) {
            const dest = await getUserDefinedDest(resource);
            uri += '?' + Base64encode(dest);
        }
    }
    return launchNativeRdpUri(uri);
}

function launchJavaActiveXRdp(resource: RdpModel) {
    const javaClient = resource.clientType === VDI_CLIENT.JAVA ? '1' : '0';

    let height = resource.height;
    let width = resource.width;
    if (resource.percent) {
        height = (screen.height * resource.percent) / 100;
        width = (screen.width * resource.percent) / 100;
    }
    const link = `/vdesk/resource_all_info.eui?resourcename=${resource.id}%23${javaClient}&resourcetype=${resource.type}`;
    const features = `height=${height}, width=${width}, toolbar=0, location=0, menubar=0`;
    window.open(link, '_blank', features);
}

async function launchRDPResource(resource: RdpModel) {
    if (resource.serverType !== SERVERTYPE.WEB_ACCESS) {
        if (resource.clientType === VDI_CLIENT.NATIVE) {
            return launchNativeRdp(resource);
        } else {
            return launchJavaActiveXRdp(resource);
        }
    }
}

interface ICredentials {
    username: string;
    password: string;
    domain: string;
}

async function launchResourceList(resource: RemoteDesktopModel): Promise<void> {
    let credentials;
    if (!resource.autoLogon) {
        /* no error handling here: throw any real error or just dialog closing */
        credentials = await getUserCredentials(resource);
    }
    //
    try {
        const loadedResources = await fetchResourceList(resource, credentials);
        /*
         * Mark first app for autoLaunch if parent resource has autoLaunch flag.
         */
        if (loadedResources.length > 0 && resource.autoLaunch) {
            loadedResources[0].props.auto_launch = '1';
        }

        /* renderReceivedFavorites */
        const resources = {};
        loadedResources.forEach((item, idx) => {
            resources[item.props.id] = item;
        });

        app.dispatch(EventTags.RESOURCE_INFO_RECEIVED, {
            method: 'replace',
            replace: { type: ResourceType.REMOTE_DESKTOP, id: resource.id },
            resourceMap: {
                [ResourceType.REMOTE_DESKTOP]: {
                    param: '',
                    resources,
                },
            },
        });

        markFavoriteAsVisited(resource.id);
    } catch (reason) {
        const vdiError = VdiErrorType.fromError(reason);
        /* Disable autoLogon if login failed */
        if (resource.autoLogon && vdiError === VdiErrorType.LOGON_FAILED) {
            resource.autoLogon = false;
        }
        // errorInfo.errorDetail = getResponseHeader("Error-Detail"); ???
        throw reason;
    }
}

/**
 * Properties that could be received from plugin for any VDI resource type
 */
type IVdiResourceItem =
    | {
          // Citrix
          caption: string;
          description: string;
          folder: string;
          iconUri: string;
          launchCookie: string;
          launchUri: string;
          restartUri?: string;
      }
    | {
          // MSRDP
          caption: string;
          description: string;
          folder: string;
          iconUri: string;
          launchUri: string;
      }
    | {
          // VMware View
          caption: string;
          description: string;
          iconUri?: string;
          html5ClientUri: string;
          nativeClientUri: string;
          windowName?: string;
      };

interface IVdiResourceList {
    items: IVdiResourceItem[];
    logoffUri?: string;
}

function fetchResourceList(resource: RemoteDesktopModel, credentials?: ICredentials): Promise<IResourceProps[]> {
    // this.onAppListStart();
    const authData =
        credentials && credentials.username
            ? [credentials.domain, credentials.username, credentials.password].join('\n')
            : null;

    return request(`/f5vdi/${resource.rdType}/resource${resource.id}`, {
        body: authData,
        method: 'POST',
        timeout: VDI.timeout,
    })
        .then((xhr): IVdiResourceList => JSON.parse(xhr.responseText))
        .then((rdResource) => {
            // report JSON error as 'unspecified'
            if (!rdResource.items || !rdResource.items.length) {
                throw new Error(VdiErrorType.NO_RESOURCES);
            }
            resource.vdiLogoffUrl = rdResource.logoffUri;
            // Define defaults applicable for any resource type.
            const defaultProps: any = {
                termtype: resource.rdType,
                type: resource.type,
                parent: resource.id,
                folder: '',
                restartUri: '',
                windowName: '',
                auto_launch: '0',
                auto_logon: '0',
                iconUri: '',
                // defaults for RDP resource constructor
                server_type: SERVERTYPE.SESSION_HOST,
                user_defined_dst: '0',
            };

            // copy items
            const resItems = rdResource.items.map(
                (item, idx): IResourceProps => ({
                    props: {
                        id: `${resource.id}-autogen-app-${idx}`,
                        custom_img_path: item.iconUri || VDI.icon,
                        ...defaultProps,
                        ...item,
                    },
                })
            );
            return resItems;
        });
}

/**
 * Show destination dialog
 */
function getUserDefinedDest(resource: RdpModel) {
    // show destination dialog and then call 'launchNativeRdp' with 'destination'
    return app.showModal(DialogType.VDI_DESTINATION, { resource }).then((result): string => {
        if (result && typeof result.destination === 'string') {
            return result.destination;
        }
        throw new Error('VDI Destination request: No destination specified');
    });
}

/**
 * Show Logon Dialog
 */
function getUserCredentials(resource: RemoteDesktopModel) {
    return app.showModal<ICredentials>(DialogType.VDI_LOGON, { resource }).then((result) => {
        /* Validate the result */
        if (typeof result === 'undefined' /*||
                    typeof result.username === 'undefined'*/) {
            throw new Error('No Credentials Specified');
        }
        return result;
    });
}

async function getPreferredClient(resource: RemoteDesktopModel, page: IWebtopModel): Promise<IVdiClient | undefined> {
    function isVdiClient(value: any): value is VDI_CLIENT {
        return Object.values(VDI_CLIENT).indexOf(value) >= 0;
    }

    let userPreferredClient: string | undefined;
    let clients: VdiClientInfo | null = null;

    if (resource.rdType === RdType.CITRIX) {
        clients = await getCitrixClients();
        userPreferredClient = page.webtop.vdi.strings.citrix_admin_preferred_client;
    } else if (resource.rdType === RdType.VMWARE_VIEW) {
        clients = await getViewClients();
        userPreferredClient = page.webtop.vdi.strings.view_admin_preferred_client;
    } else {
        // RDP does not have HTML5 client
        return {
            name: 'RDP',
            launch: launchRDPResource as LaunchFunction,
        };
    }
    const availableClients = Object.keys(clients).filter(isVdiClient);

    // Nothing to choose, only one client is configured
    if (availableClients.length === 1) {
        return clients[availableClients[0]];
    }

    // Check if admin preferred client is set and configured
    if (isVdiClient(userPreferredClient) && clients[userPreferredClient]) {
        return clients[userPreferredClient];
    }

    userPreferredClient = getUserPreferredClient(resource.rdType);
    // Check if user preferred client is set and configured
    if (userPreferredClient && clients[userPreferredClient]) {
        return clients[userPreferredClient];
    }

    /* Value not found; ask user */
    return app.showModal(DialogType.VDI_LAUNCH_CLIENT, { resource }).then((result) => {
        if (result && isVdiClient(result[resource.rdType])) {
            return clients![result[resource.rdType]];
        }
        throw new Error('VDI: Client not selected');
    });
}

export default launchRDResource;
